export type CatalogSong = {
  id?: string;
  _id?: string;
  legacyId?: number;
  slug: string;
  songTitle: string;
  artistSlug: string;
  artistName: string;
  albumName?: string;
  songTags?: string;
  songInfo?: string;
  content?: string;
  translation_es?: string;
  translationEs?: string;
  openAIResponse?: string;
  youtubeId?: string;
  hitsCount?: number;
  coverImage?: string;
  song_thumbnail_image?: string;
  artist_profile_image?: string;
  artistTopSongs?: CatalogSong[];
  created?: string;
  isImported?: boolean;
  isExternal?: false;
  provider?: 'mongo' | 'genius' | 'itunes';
  // Multi-language translations keyed by ISO code (e.g. translations.es).
  // translationEs stays populated as a legacy mirror of translations.es.
  translations?: Record<string, string>;
  // Per-language meaning. Legacy `openAIResponse` is treated as `meanings.es`
  // when this map doesn't have an `es` entry. New writes populate both.
  meanings?: Record<string, string>;
  // ISO 639-1 detected language of the original lyrics (es/en/pt).
  // Used by the frontend translation tab to skip the translation network
  // call entirely when the user-requested language equals the source.
  sourceLanguage?: 'es' | 'en' | 'pt' | string;
  media?: {
    artistImage?: string;
    songThumbnailImage?: string;
    songImage?: string;
    artistDescription?: string;
    geniusSongId?: number;
    geniusArtistId?: number;
  };
};

export type ExternalSongResult = {
  externalId: number;
  provider: 'genius' | 'itunes';
  songTitle: string;
  fullTitle: string;
  artistName: string;
  thumbnailUrl?: string;
  // Present on iTunes results — 30-second m4a preview. Absent on Genius.
  previewUrl?: string;
  // Present on iTunes results — album name comes from the API.
  albumName?: string;
  isExternal: true;
  isImported: false;
};

export type SearchResponse = {
  results: CatalogSong[];
  external: ExternalSongResult[];
  pageInfo: {
    page: number;
    limit: number;
    totalResults: number;
    totalPages: number;
    externalCount: number;
    externalAttachedToPage: boolean;
  };
};

const mainServiceURL = import.meta.env.VITE_MAIN_SERVICE_URL || '';
const publicGatewayService = import.meta.env.VITE_MIXGENIE_PUBLIC_GATEWAY_SERVICE || 'mixgenie-service';

async function callCatalog(
  path: string,
  options: { method?: string; body?: unknown; timeoutMs?: number } = {},
) {
  const headers: HeadersInit = {};
  if (options.body) headers['Content-Type'] = 'application/json';

  // Client-side timeout — the song-catalog import path chains up to four
  // external lyric providers (lyrics.ovh → ReadDork → Genius-via-Scrappey
  // → Letras-via-Scrappey → PromptLab). If any stalls, without an abort
  // the FE spinner sits forever. Default 15s for reads, override for
  // known-slow endpoints (import).
  const timeoutMs = options.timeoutMs ?? 15_000;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(
      `${mainServiceURL}/public-gateway/${publicGatewayService}/public/song-catalog/${path.replace(/^\//, '')}`,
      {
        method: options.method || 'GET',
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      },
    );
  } catch (err: any) {
    window.clearTimeout(timeoutId);
    if (err?.name === 'AbortError') {
      throw new Error('Taking longer than expected — try another song or search again in a moment.');
    }
    throw err;
  }
  window.clearTimeout(timeoutId);

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(json?.message || json?.error || 'Song catalog request failed');
  }
  return json?.data ?? json;
}

export async function searchSongs(query: string, page = 1, limit = 10): Promise<SearchResponse> {
  const params = new URLSearchParams({
    searchTitle: query,
    page: String(page),
    limit: String(limit),
  });
  const payload = await callCatalog(`search?${params.toString()}`);
  // Server returns { results, external, pageInfo }. Default external to []
  // for safety if an older backend is still serving us.
  return {
    results: payload?.results || [],
    external: payload?.external || [],
    pageInfo: payload?.pageInfo || { page, limit, totalResults: 0, totalPages: 0, externalCount: 0, externalAttachedToPage: false },
  };
}

export async function importExternalSong(
  provider: 'genius' | 'itunes',
  externalId: number,
): Promise<{ artistSlug: string; slug: string; songId: string; created: boolean }> {
  // With the two-phase import (fast metadata insert + background lyric
  // enrichment), the import call itself returns in ~1-2s. Keep a
  // generous 15s cap for safety on slow connections.
  return callCatalog('import', { method: 'POST', body: { provider, externalId }, timeoutMs: 15_000 });
}

export async function getSong(artistSlug: string, slug: string): Promise<CatalogSong | null> {
  try {
    const payload = await callCatalog(`song/${encodeURIComponent(slug)}/${encodeURIComponent(artistSlug)}`);
    return payload?.data || payload;
  } catch {
    return null;
  }
}

export async function getArtistSongs(artistSlug: string, page = 1, limit = 24) {
  const params = new URLSearchParams({
    artistSlug,
    page: String(page),
    limit: String(limit),
  });
  return callCatalog(`artist?${params.toString()}`);
}

export async function getLatestSongs(limit = 24): Promise<CatalogSong[]> {
  const payload = await callCatalog(`songs/latest?limit=${limit}`);
  return Array.isArray(payload) ? payload : payload?.data || [];
}

export async function getTrendingSongs(limit = 24): Promise<CatalogSong[]> {
  const payload = await callCatalog(`songs/trending?limit=${limit}`);
  return Array.isArray(payload) ? payload : payload?.data || [];
}

export async function getTrendingWeeklySongs(limit = 15): Promise<CatalogSong[]> {
  const payload = await callCatalog(`songs/trending-weekly?limit=${limit}`);
  return Array.isArray(payload) ? payload : payload?.data || [];
}

export async function getArtistsByLetter(letter: string, page = 1, limit = 102) {
  const params = new URLSearchParams({
    artistName: letter,
    page: String(page),
    limit: String(limit),
  });
  return callCatalog(`artist-letter-wise?${params.toString()}`);
}

export async function trackSongHit(song: CatalogSong) {
  const id = song.id || song._id || song.legacyId;
  if (!id) return;
  await callCatalog(`songs/${encodeURIComponent(String(id))}/hit`, { method: 'POST' }).catch(() => {});
}

// ── Lazy enrichment ─ called by SongDetailPage when a field is missing ──

export async function refreshSongLyrics(songId: string): Promise<{ lyrics: string | null; updated: boolean }> {
  return callCatalog(`song/${encodeURIComponent(songId)}/refresh-lyrics`, { method: 'POST' });
}

export async function refreshSongMeaning(
  songId: string,
  language?: string,
): Promise<{ meaning: string | null; language?: string; updated: boolean; reason?: string }> {
  // Per-language endpoint; legacy unsuffixed path still works and defaults
  // to Spanish — useful for old code paths but new code should always pass `language`.
  const path = language
    ? `song/${encodeURIComponent(songId)}/refresh-meaning/${encodeURIComponent(language)}`
    : `song/${encodeURIComponent(songId)}/refresh-meaning`;
  return callCatalog(path, { method: 'POST' });
}

export async function refreshSongTranslation(
  songId: string,
  language: string,
): Promise<{ translation: string | null; language: string; updated: boolean; reason?: string }> {
  return callCatalog(`song/${encodeURIComponent(songId)}/refresh-translation/${encodeURIComponent(language)}`, { method: 'POST' });
}

export async function discoverSongImages(songId: string): Promise<{
  updated: boolean;
  reason?: 'has-images' | 'no-match' | 'genius-failed' | 'mirror-failed';
  geniusSongId?: number;
  media?: { artistImage?: string | null; songThumbnailImage?: string | null; songImage?: string | null };
}> {
  return callCatalog(`song/${encodeURIComponent(songId)}/discover-images`, { method: 'POST' });
}

export function stripHtml(value?: string) {
  return (value || '').replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Normalize a Mongo ObjectId out of a CatalogSong regardless of the wire
 * shape. Backends serving raw Mongo documents via NestJS often emit
 * `_id` as `{ buffer: { type:'Buffer', data:[…12 bytes…] } }` instead of a
 * hex string — `String(song._id)` then becomes "[object Object]" and
 * downstream ObjectId validation rejects it. This helper handles:
 *   - string form ("6a12c24a4590c940d183e0ef")
 *   - Buffer-JSON form ({buffer:{type:'Buffer',data:[...]}} or {data:[...]})
 *   - EJSON form ({$oid:'...'})
 *   - Mongoose ObjectId-like objects with toString() → hex
 * Returns undefined if no valid 24-hex id can be extracted.
 */
export function songObjectId(song?: {
  id?: unknown;
  _id?: unknown;
} | null): string | undefined {
  const candidates: unknown[] = [song?._id, song?.id];
  for (const c of candidates) {
    const s = toObjectIdString(c);
    if (s) return s;
  }
  return undefined;
}

function toObjectIdString(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string' && /^[a-f0-9]{24}$/i.test(v)) return v.toLowerCase();
  if (typeof v !== 'object') return undefined;
  const o = v as Record<string, unknown>;
  // EJSON: { $oid: '...' }
  if (typeof o.$oid === 'string' && /^[a-f0-9]{24}$/i.test(o.$oid)) return (o.$oid as string).toLowerCase();
  // Buffer-JSON: { data: [12 bytes] } or { buffer: { data: [12 bytes] } }
  const buf: unknown = (o.data as unknown) || ((o.buffer as Record<string, unknown> | undefined)?.data as unknown);
  if (Array.isArray(buf) && buf.length === 12 && buf.every((b) => typeof b === 'number')) {
    return (buf as number[]).map((b) => (b & 0xff).toString(16).padStart(2, '0')).join('');
  }
  // Fallback: obj.toString() may give hex on Mongoose ObjectIds
  try {
    const s = String((o as { toString: () => string }).toString());
    if (/^[a-f0-9]{24}$/i.test(s)) return s.toLowerCase();
  } catch { /* noop */ }
  return undefined;
}

export function generatorReferencePath(
  song: Pick<CatalogSong, 'songTitle' | 'artistName' | 'songTags' | 'albumName' | 'openAIResponse' | 'slug' | 'artistSlug' | 'media' | 'coverImage' | 'song_thumbnail_image'>,
) {
  const params = new URLSearchParams({
    referenceMode: 'title-artist',
    referenceTitle: song.songTitle || '',
    referenceArtist: song.artistName || '',
  });
  if (song.slug) params.set('referenceSlug', song.slug);
  if (song.artistSlug) params.set('referenceArtistSlug', song.artistSlug);
  const thumb = song.coverImage || song.song_thumbnail_image || song.media?.songThumbnailImage;
  if (thumb) params.set('referenceCover', thumb);
  // Only pass compact styling hints (tags + album). `openAIResponse` is a
  // paragraph-long *meaning* analysis of the song, not a stylistic hint —
  // including it blew past Mureka's 1024-char prompt limit. If we want to
  // seed style further in the future, add a dedicated `styleTags` field
  // to SongCatalog rather than reusing meaning text.
  const styleNotes = [song.songTags, song.albumName].filter(Boolean).join(' · ').slice(0, 200);
  if (styleNotes) params.set('styleNotes', styleNotes);
  return `/generator?${params.toString()}`;
}

export function createSimilarSignupPath(song: CatalogSong) {
  return `/register?next=${encodeURIComponent(generatorReferencePath(song))}`;
}
